import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RulePicker } from './RulePicker.js';

interface DeploymentRow {
  readonly deploymentId: string;
  readonly ruleCode: string | null;
  readonly ruleName: string | null;
  readonly deploymentType: string;
  readonly status: string;
}

export function M24DeploymentDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [deployments, setDeployments] = useState<DeploymentRow[]>([]);
  const [ruleId, setRuleId] = useState('');
  const [ruleLabel, setRuleLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: DeploymentRow[] }>('/v1/rule-deployments', { token })
      .then((r) => { if (alive) { setDeployments(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => load(), [load]);

  function onDeploy(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    apiRequest<{ data: DeploymentRow }>('/v1/rule-deployments', {
      method: 'POST',
      token,
      body: { ruleId },
    })
      .then((r) => { setMessage(t('m24.deployment.msgResult', { status: r.data.status, rule: ruleLabel })); setRuleId(''); load(); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setSubmitting(false));
  }

  return (
    <main aria-label="deployment-dashboard">
      <h1>{t('m24.deployment.title')}</h1>
      <form aria-label="deploy-form" onSubmit={onDeploy}>
        <RulePicker label={t('m24.deployment.ruleId')} value={ruleId} onPick={(id, label) => { setRuleId(id); setRuleLabel(label); }} />
        <button type="submit" disabled={submitting || !ruleId.trim()}>{t('m24.deployment.submit')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m24.deployment.loading')}</p> : (
        <table aria-label="deployment-table">
          <thead>
            <tr><th>{t('m24.deployment.colRule')}</th><th>{t('m24.deployment.colType')}</th><th>{t('m24.deployment.colStatus')}</th></tr>
          </thead>
          <tbody>
            {deployments.map((d) => (
              <tr key={d.deploymentId}>
                <td>{[d.ruleCode, d.ruleName].filter(Boolean).join(' \u2014 ')}</td>
                <td>{d.deploymentType}</td>
                <td>{d.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
